import React, { useState, useEffect } from "react";
import {
  FileTextOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  LoadingOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileOutlined,
} from "@ant-design/icons";
import { Button } from "antd";
import deleteListItems from "./../../../assets/svg/deleteListItems.svg";
import { IMAGE_TYPES } from "../../../constants/filesExtensions";
const FileListViewer = ({ data }) => {
  const {
    fileList,
    currentTotalSize,
    uploading,
    removeAllFiles,
    removeFile,
    getFileStatus,
    formatFileSize,
    getFileType,
  } = data || {};

  const [previews, setPreviews] = useState({});

  // Generate preview for image files
  useEffect(() => {
    if (!fileList) return;

    const generatePreviews = async () => {
      const newPreviews = {};

      for (const file of fileList) {
        if (file.type && file.type.startsWith("image/")) {
          try {
            const url = URL.createObjectURL(file);
            newPreviews[file.name] = url;
          } catch (error) {
            console.error("Error creating preview:", error);
          }
        }
      }

      setPreviews(newPreviews);
    };

    generatePreviews();

    // Cleanup URLs on unmount
    return () => {
      Object.values(previews).forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, [fileList]);

  // Get file icon based on type
  const getFileIcon = (fileName, fileType) => {
    const extension = fileName.split(".").pop()?.toLowerCase();

    if (fileType?.startsWith("image/") || IMAGE_TYPES.includes(extension)) {
      return <FileImageOutlined />;
    } else if (extension === "pdf") {
      return <FilePdfOutlined />;
    } else if (["doc", "docx"].includes(extension)) {
      return <FileWordOutlined />;
    } else if (["xls", "xlsx"].includes(extension)) {
      return <FileExcelOutlined />;
    } else {
      return <FileOutlined />;
    }
  };

  const mockGetFileStatus = (fileName) => {
    const statuses = ["pending", "success", "uploading", "failed"];
    return statuses[Math.floor(Math.random() * statuses.length)];
  };

  // Demo data if no data provided
  const demoFileList = fileList || [];

  const demoUploading = uploading || false;

  if (!demoFileList || demoFileList.length === 0) return null;

  return (
    <div
      className="multiple-files-container"
      style={{
        margin: "20px auto",
        borderRadius: "8px",
        width: "100%",
      }}
    >
      <div
        className="files-list"
        style={{ display: "flex", flexDirection: "column", gap: "12px" }}
      >
        {demoFileList.map((file, index) => {
          const status = (getFileStatus || mockGetFileStatus)(file.name);
          const isImage =
            file.type?.startsWith("image/") ||
            IMAGE_TYPES.includes(file.name.split(".").pop()?.toLowerCase());

          return (
            <div
              key={index}
              className="Uploaded-file-list"
              style={{
                display: "flex",
                alignItems: "center",
                padding: "12px",
                borderRadius: "6px",
                transition: "all 0.2s ease",
              }}
            >
              {/* File Preview */}
              <div
                className="file-preview"
                style={{
                  width: "48px",
                  height: "48px",
                  marginRight: "12px",
                  borderRadius: "4px",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f5f5f5",
                  border: "1px solid #e0e0e0",
                }}
              >
                {isImage && previews[file.name] ? (
                  <img
                    src={previews[file.name]}
                    alt={file.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div style={{ fontSize: "20px", color: "#666" }}>
                    {getFileIcon(file.name, file.type)}
                  </div>
                )}
              </div>

              <div
                className="file-item-content"
                style={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div className="file-info">
                  <div className="file-name-section">
                    <h4
                      className="file-name"
                      style={{
                        margin: "0 0 4px 0",
                        fontSize: "14px",
                        fontWeight: "500",

                        wordBreak: "break-all",
                        textAlign: "left",
                      }}
                    >
                      {file.name}
                    </h4>
                    <div
                      className="file-meta"
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <span>{formatFileSize(file.size || formatFileSize)}</span>
                    </div>
                  </div>
                </div>

                <div className="file-status">
                  {status === "success" && (
                    <div
                      className="status-success"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        color: "#52c41a",
                        fontSize: "12px",
                      }}
                    >
                      <CheckCircleOutlined />
                      <span>Uploaded</span>
                    </div>
                  )}
                  {status === "failed" && (
                    <div
                      className="status-failed"
                      style={{
                        color: "#ff4d4f",
                        fontSize: "12px",
                      }}
                    >
                      <span>Failed</span>
                    </div>
                  )}
                  {status === "uploading" && (
                    <div
                      className="status-uploading"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        color: "#1890ff",
                        fontSize: "12px",
                      }}
                    >
                      <LoadingOutlined spin />
                      <span>Uploading...</span>
                    </div>
                  )}
                  {status === "pending" && !demoUploading && (
                    <div className="status-pending">
                      <Button
                        type="text"
                        icon={<img src={deleteListItems} />}
                        onClick={() =>
                          (
                            removeFile ||
                            (() => console.log("Remove file", index))
                          )(index)
                        }
                        size="small"
                        style={{ color: "#ff4d4f" }}
                      />
                    </div>
                  )}
                  {status === "pending" && demoUploading && (
                    <div
                      className="status-waiting"
                      style={{
                        color: "#faad14",
                        fontSize: "12px",
                      }}
                    >
                      <span>Waiting...</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FileListViewer;
